import React, { useState } from 'react';
import { Input, Modal, Spin, Select, Table, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePW9221F1Form } from './hooks/usePW9221F1Form';
import { pw9221f1Api } from '../../services/pw9221f1Api';
import LookupField from '../../components/LookupField';
import { SharedActionBar } from '../../components/SharedActionBar';
import type { ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { useModulePermission } from '../../hooks/usePermission';

const { TextArea } = Input;

const PW9221F1Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const perm = useModulePermission('pw9221f1');
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    empID, setEmpID,
    leaveType, setLeaveType,
    startDate, setStartDate,
    startTime, setStartTime,
    endDate, setEndDate,
    endTime, setEndTime,
    totalLeave, setTotalLeave,
    unit, setUnit,
    reason, setReason,
    agent, setAgent,
    deptCode, setDeptCode,
    approveLevel, setApproveLevel,
    approveReason, setApproveReason,
    getStatusLabel,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch, loadRecords,
  } = usePW9221F1Form();

  const [searchText, setSearchText] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Load review record if ?review= is present
  React.useEffect(() => {
    if (reviewId) {
      quickSearch(reviewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  // 審核狀態：F921_30 規格五值【全部法定，沒有舊碼/新碼之分】（批次 5 修正，2026-06-12）
  //   ''=尚未處理（待審）、V=已初核、P=已審核未複核（中間態，非終態核准！）、
  //   Y=已核准（准假）、N=不准假（終態，非草稿）
  // 舊自創碼 S(送審)/R(退回) 已廢除：送審→''、退回→N、核准→Y
  const statusCode = (current?.f921_30 || '').trim();
  const approval: ApprovalState = {
    statusCode,
    isApproved: statusCode === 'Y',                              // Y=已核准（准假，終態）
    isPending: !!current && ['', 'V', 'P'].includes(statusCode), // 待審/初核/審核未複核皆屬流程中
    isLocked: ['Y', 'N'].includes(statusCode),                   // 終態鎖定（與後端一致）
  };

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除請假單「${current.f921_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const handleSubmit = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將請假單 ${current.f921_01} 送審？`,
      okText: '送審', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9221f1Api.submitForApproval(current.f921_01);
          message.success(resp.message || '送審成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '送審失敗');
        }
      },
    });
  };

  const handleApprove = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准請假單 ${current.f921_01}？`,
      okText: '核准', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9221f1Api.approve(current.f921_01);
          message.success(resp.message || '核准成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '核准失敗');
        }
      },
    });
  };

  const handleReject = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回請假單 ${current.f921_01}？`,
      okText: '退回', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9221f1Api.reject(current.f921_01);
          message.success(resp.message || '退回成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '退回失敗');
        }
      },
    });
  };

  const handleWriteoff = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${current.f921_01}？沖銷後可重新送審。`,
      okText: '沖銷', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9221f1Api.writeoff(current.f921_01);
          message.success(resp.message || '沖銷成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '沖銷失敗');
        }
      },
    });
  };

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6" style={{ paddingBottom: 80 }}>
        <h2 className="text-xl font-bold mb-4">
          請假單 (pw9221f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}]
            {records.length > 0 ? ` ${currentIdx + 1} / ${records.length}` : ' 無資料'}
          </span>
        </h2>

        {/* Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入單號、員工代號或事由搜尋"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isEditing}
            style={{ width: 300 }}
          />
          <button className="sab-btn" onClick={handleSearch} disabled={isEditing}>查詢</button>
        </div>

        {/* Form Fields */}
        <div className="space-y-3 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                請假單號 (F921_01)
              </label>
              <Input value={current?.f921_01 || ''} disabled style={{ width: '100%' }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                員工代號 (F921_02) <span className="text-red-500">*</span>
              </label>
              <Input
                value={empID} onChange={e => setEmpID(e.target.value)}
                maxLength={8} disabled={mode !== 'add'} style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">申請日期 (F921_11)</label>
              <Input
                type="date" value={current?.f921_11?.substring(0, 10) || ''}
                disabled style={{ width: '100%' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">入單日 (F921_92)</label>
              <Input value={current?.f921_92?.substring(0, 10) || ''} disabled style={{ width: '100%' }} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                假別 (F921_12) <span className="text-red-500">*</span>
              </label>
              <LookupField lookupType="leaveType" value={leaveType as string || ''}
                onChange={(code) => setLeaveType(code)}
                disabled={!isEditing} style={{ width: '100%' }} title="假別搜尋" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">單位 (F921_18)</label>
              <Input
                value={unit}
                maxLength={4} disabled style={{ width: '100%' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">總請假數 (F921_19)</label>
              <Input
                type="number" value={totalLeave}
                disabled style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                開始日 (F921_13) <span className="text-red-500">*</span>
              </label>
              <Input
                type="date" value={startDate}
                onChange={e => setStartDate(e.target.value)}
                disabled={!isEditing} style={{ width: '100%' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">時間 (F921_14)</label>
              <Input
                value={startTime} onChange={e => setStartTime(e.target.value)}
                maxLength={5} placeholder="HH:MM" disabled={!isEditing} style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                結束日 (F921_15) <span className="text-red-500">*</span>
              </label>
              <Input
                type="date" value={endDate}
                onChange={e => setEndDate(e.target.value)}
                disabled={!isEditing} style={{ width: '100%' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">時間 (F921_16)</label>
              <Input
                value={endTime} onChange={e => setEndTime(e.target.value)}
                maxLength={5} placeholder="HH:MM" disabled={!isEditing} style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">代理人 (F921_10)</label>
              <Select
                value={agent || undefined}
                onChange={(val: string) => setAgent(val || '')}
                disabled={!isEditing}
                allowClear
                showSearch
                placeholder="選擇代理人"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">部門代號 (F921_31)</label>
              <Input
                value={deptCode} onChange={e => setDeptCode(e.target.value)}
                maxLength={4} disabled={mode !== 'add'} style={{ width: '100%' }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              事由 (F921_21) <span className="text-red-500">*</span>
            </label>
            <TextArea
              value={reason} onChange={e => setReason(e.target.value)}
              maxLength={254} rows={3} disabled={!isEditing} style={{ width: '100%' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">核准層級 (F921_29)</label>
              <Input
                value={approveLevel} onChange={e => setApproveLevel(e.target.value)}
                maxLength={1} disabled={!isEditing} style={{ width: '100%' }}
                placeholder="A=須總經理核准"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">須複核原因 (F921_27)</label>
              <Input
                value={approveReason}
                maxLength={30} disabled style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        {/* Audit & Approval info (browse mode) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1 border-t pt-3">
            <div>初核: {current.f921_22 || '-'} | 核准日: {current.f921_23?.substring(0, 10) || '-'}</div>
            <div>複核: {current.f921_25 || '-'} | 核准日: {current.f921_26?.substring(0, 10) || '-'}</div>
            <div>審核: {current.f921_72 || '-'} | 核准日: {current.f921_73?.substring(0, 10) || '-'}</div>
            <div>入單人員: {current.f921_91} | 入單日: {current.f921_92?.substring(0, 10) || '-'}</div>
          </div>
        )}

        {/* Leave Balance Grid (f9131) */}
        {current && mode === 'browse' && (
          <div className="mb-4">
            <h3 className="text-sm font-bold mb-2">假別餘額</h3>
            <Table
              dataSource={[]}
              columns={[
                { title: '員工編號', dataIndex: 'f9131_01', key: 'f9131_01', width: 80 },
                { title: '年度', dataIndex: 'f9131_02', key: 'f9131_02', width: 70 },
                { title: '假別', dataIndex: 'f9131_03', key: 'f9131_03', width: 80 },
                { title: '可用數', dataIndex: 'f9131_11', key: 'f9131_11', width: 70 },
                { title: '已用(核准數)', dataIndex: 'f9131_14', key: 'f9131_14', width: 120 },
              ]}
              pagination={false} size="small" rowKey={(_, i) => String(i)}
              locale={{ emptyText: '無假別餘額資料' }}
            />
          </div>
        )}

        {/* Monthly Detail Grid (f9211) */}
        {current && (
          <div className="mb-4">
            <h3 className="text-sm font-bold mb-2">月份分段明細 ({current.details?.length || 0} 筆)</h3>
            <Table
              dataSource={current.details || []}
              columns={[
                { title: '年月', dataIndex: 'f9211_03', key: 'f9211_03', width: 100 },
                { title: '本次請假數', dataIndex: 'f9211_12', key: 'f9211_12', width: 120 },
              ]}
              pagination={false} size="small" rowKey={(_, i) => String(i)}
              locale={{ emptyText: '無明細資料' }}
            />
          </div>
        )}
      </div>

      {/* SharedActionBar */}
      <SharedActionBar
        mode={mode} isEditing={isEditing} hasRecord={!!current}
        currentIdx={currentIdx} recordCount={records.length}
        onNavFirst={navFirst} onNavPrev={navPrev}
        onNavNext={navNext} onNavLast={navLast}
        onAdd={startAdd} onModify={startEdit} onDelete={handleDelete}
        onSave={save} onCancel={cancel}
        approval={approval}
        onSubmit={handleSubmit} onApprove={handleApprove}
        onReject={handleReject} onWriteoff={handleWriteoff} onHistory={() => setShowHistory(true)}
        onExit={() => navigate(-1)}
        canCreate={perm.canCreate} canEdit={perm.canEdit}
        canDelete={perm.canDelete} canApprove={perm.canApprove}
      />

      {/* Approval History Modal */}
      <ApprovalHistoryModal
        visible={showHistory}
        docNo={current?.f921_01 || ''}
        onClose={() => setShowHistory(false)}
      />
    </Spin>
  );
};

export default PW9221F1Form;
